import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { MeterType } from '@models/meter.model';
import { LoginType } from '@models/registration.models';
import { ConsentType as SmartMeterConsentType } from '@models/consent.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

export interface Preferences {
  sdkWindowType: 'tab' | 'popup' | 'iframe';
  loginType: LoginType;
  supportedMeterTypes: MeterType[];
  supportedSmartMeterConsentTypes: SmartMeterConsentType[];
  websiteAddress?: string; // Optional field for website address
  isStandaloneMode: boolean; // true = standalone/regular auth, false = SDK/embedded mode
  bypassEmailDomainValidation: boolean; // true = skip the email/company domain-match check during registration
}

const DEFAULT_PREFERENCES: Preferences = {
  sdkWindowType: 'iframe',
  loginType: LoginType.EMAIL_LINK_AND_OTP,
  supportedMeterTypes: [
    MeterType.HALF_HOURLY,
    MeterType.NON_HALF_HOURLY,
    MeterType.SMART
  ],
  supportedSmartMeterConsentTypes: [
    SmartMeterConsentType.MAC_ADDRESS,
    // SmartMeterConsentType.BILL_UPLOAD,
    SmartMeterConsentType.STRIPE
  ],
  isStandaloneMode: true, // Default to standalone mode
  bypassEmailDomainValidation: false // Default to false so orgs without this preference keep today's behavior
};

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private preferences: Preferences = DEFAULT_PREFERENCES;
  http = inject(HttpClient);
  endPointDomain = environment.baseUrlOrg + '/meter-history/smart'; 

  /**
   * Load preferences from API based on client key
   * @param clientKey The client key to fetch preferences for
   * @returns Observable<Preferences> The preferences for the client
   */
  loadPreferences(clientKey: string): Observable<Preferences> {
    const url = `${environment.baseUrl}/core/v1/get-preferences`;
    const headers = {
      'Authorization': `Bearer ${clientKey}`
    };

    console.log(`Loading preferences for client key: ${clientKey}`);

    return this.http.get<any>(url, { headers }).pipe(map(response => {
      // Merge field-by-field so orgs whose stored preferences predate a newer
      // field (e.g. bypassEmailDomainValidation) still get that field's default,
      // rather than only falling back to defaults when the whole object is missing.
      this.preferences = { ...DEFAULT_PREFERENCES, ...(response?.data?.preferences ?? {}) };
      return this.preferences;
    }));
  }

  /**
   * Get current preferences
   * @returns Preferences The current preferences
   */
  getPreferences(): Preferences {
    return { ...this.preferences };
  }

  getLoginType(): LoginType {
    // const loginTypes = this.getPreferences().loginType;
    // if (loginTypes.includes(LoginType.BYPASS_EMAIL_VERIFICATION)) {
    //   // If bypass is included, return only bypass to avoid confusion
    //   return LoginType.BYPASS_EMAIL_VERIFICATION;
    // } else if (loginTypes.includes(LoginType.EMAIL_MAGIC_LINK)) {
    //   return LoginType.EMAIL_MAGIC_LINK;
    // } else {
    //   return LoginType.EMAIL_OTP;
    // }
    return this.getPreferences().loginType;
  }

  /**
   * Set standalone mode (true = standalone/regular auth, false = SDK/embedded mode)
   * @param isStandalone Whether the app is running in standalone mode
   */
  setStandaloneMode(isStandalone: boolean): void {
    this.preferences = { ...this.preferences, isStandaloneMode: isStandalone };
  }

  /**
   * Check if app is running in standalone mode
   * @returns boolean true if standalone mode, false if SDK/embedded mode
   */
  isStandalone(): boolean {
    return this.preferences.isStandaloneMode;
  }

  /**
   * Whether the email/company domain-match check during registration should be skipped
   * @returns boolean true if the domain-match check should be bypassed
   */
  shouldBypassEmailDomainValidation(): boolean {
    return this.preferences.bypassEmailDomainValidation;
  }
}
